import React from 'react';
import { Link } from 'react-router-dom';
import PageTemplate from '../../components/Common/PageTemplate';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const cardVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5,
      ease: 'easeOut',
    },
  }),
};

const ProgramCard = ({ program, index }) => {
  const { t } = useTranslation();
  return (
    <motion.div
      className="bg-white rounded-2xl shadow-lg overflow-hidden flex flex-col group"
      variants={cardVariants}
      initial="hidden"
      animate="visible"
      custom={index}
      whileHover={{ y: -8, boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}
      transition={{ duration: 0.3 }}
    >
      <div 
        className="h-48 bg-cover bg-center relative"
        style={{ backgroundImage: `url(${program.image})` }}
      >
        <img 
            src="https://kkm.hacettepe.edu.tr/wp-content/uploads/2018/07/cropped-logo-hacettepe.png" 
            alt="Hacettepe Üniversitesi Logosu"
            className="absolute top-4 left-4 w-5 h-auto"
        />
      </div>
      <div className={`p-8 flex-grow flex flex-col text-white ${program.bgColor}`}>
        <h3 className="text-2xl font-bold tracking-wide">{program.name}</h3>
        <div className={`w-12 h-1 mt-2 mb-4 ${program.lineColor}`} />
        <p className="text-white/80 mb-6 flex-grow">{program.description}</p>
        {program.external ? (
          <a href={program.link} target="_blank" rel="noopener noreferrer" className="self-start mt-auto group">
            <div className="flex items-center font-bold">
              {t('undergraduate.learnMore')}
              <div className="ml-3 bg-white/20 group-hover:bg-white transition-colors duration-300 rounded-full p-1">
                <ArrowRight size={16} className="text-white group-hover:text-black transition-colors duration-300" />
              </div>
            </div>
          </a>
        ) : (
          <Link to={program.link} className="self-start mt-auto group">
            <div className="flex items-center font-bold">
              {t('undergraduate.learnMore')}
              <div className="ml-3 bg-white/20 group-hover:bg-white transition-colors duration-300 rounded-full p-1">
                <ArrowRight size={16} className="text-white group-hover:text-black transition-colors duration-300" />
              </div>
            </div>
          </Link>
        )}
      </div>
    </motion.div>
  );
};

const UndergraduateProgramsPage = () => {
  const { t } = useTranslation();

  const programs = [
    {
      name: t('departments.biology'),
      description: t('undergraduate.program_descriptions.biology'),
      link: 'https://bilsis.hacettepe.edu.tr/oibs/bologna/index.aspx?lang=tr&curOp=showPac&curUnit=307&curSunit=321',
      external: true,
      image: '/biology.jpg',
      bgColor: 'bg-emerald-800',
      lineColor: 'bg-emerald-400',
      iconColor: 'text-emerald-800',
    },
    {
      name: t('departments.statistics'),
      description: t('undergraduate.program_descriptions.statistics'),
      link: 'https://bilsis.hacettepe.edu.tr/oibs/bologna/index.aspx?lang=tr&curOp=showPac&curUnit=307&curSunit=329#',
      external: true,
      image: '/statistic.jpg',
      bgColor: 'bg-sky-800',
      lineColor: 'bg-sky-400',
      iconColor: 'text-sky-800',
    },
    {
      name: t('departments.chemistry'),
      description: t('undergraduate.program_descriptions.chemistry'),
      link: 'https://bilsis.hacettepe.edu.tr/oibs/bologna/index.aspx?lang=tr&curOp=showPac&curUnit=307&curSunit=333',
      external: true,
      image: '/chemistry.jpg',
      bgColor: 'bg-purple-800',
      lineColor: 'bg-purple-400',
      iconColor: 'text-purple-800',
    },
    {
      name: t('departments.mathematics'),
      description: t('undergraduate.program_descriptions.mathematics'),
      link: 'https://bilsis.hacettepe.edu.tr/oibs/bologna/index.aspx?lang=tr&curOp=showPac&curUnit=307&curSunit=328#',
      external: true,
      image: '/matematik.jpg',
      bgColor: 'bg-red-800',
      lineColor: 'bg-red-400',
      iconColor: 'text-red-800',
    },
    {
      name: t('departments.actuarial_science'),
      description: t('undergraduate.program_descriptions.actuarial_science'),
      link: 'https://bilsis.hacettepe.edu.tr/oibs/bologna/index.aspx?lang=tr&curOp=showPac&curUnit=307&curSunit=381',
      external: true,
      image: '/aktuerya.jpg',
      bgColor: 'bg-amber-800',
      lineColor: 'bg-amber-400',
      iconColor: 'text-amber-800',
    },
  ];

  return (
    <PageTemplate
      title={t('undergraduate.title')}
      subtitle={t('undergraduate.subtitle')}
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 my-16">
        {programs.map((program, index) => (
          <ProgramCard key={program.name} program={program} index={index} />
        ))}
      </div>
    </PageTemplate>
  );
};

export default UndergraduateProgramsPage; 